/**
 * Las veces que se ha jugado este cuestionario, **dentro del propio cuestionario**.
 *
 * Vivía en una ruta aparte, `/cuestionarios/:quizId/partidas`, a la que se llegaba por un enlace
 * pequeño de la lista. Eso obligaba a saber que existía, y con ella se perdía todo el contexto: la
 * pantalla del histórico no enseñaba ni una pregunta del cuestionario al que pertenecía. Aquí está
 * donde se busca, que es al final de lo que se acaba de preparar.
 *
 * Sirve para lo que §11 llama comparar entre grupos: el mismo cuestionario jugado con dos clases
 * distintas dice más sobre la pregunta que sobre las clases.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { listGames, type GameSummary } from '../analytics/api.js';
import { Glifo, type NombreGlifo } from './sistema/glifo.js';
import { Rotulo } from './sistema/rotulo.js';
import { useRecurso, VistaRecurso } from './sistema/recurso.js';

/** Cómo se ve cada estado: glifo y palabra, nunca solo el color. */
const ESTADOS = {
  lobby: { glifo: 'vacio', clave: 'analytics.stateOpen' },
  running: { glifo: 'medio', clave: 'analytics.stateRunning' },
  ended: { glifo: 'correcto', clave: 'analytics.stateEnded' },
  abandoned: { glifo: 'prohibido', clave: 'analytics.stateAbandoned' },
} as const satisfies Readonly<
  Record<string, { readonly glifo: NombreGlifo; readonly clave: string }>
>;

export function HistorialDePartidas({ quizId }: { quizId: string }): React.JSX.Element {
  const { t, i18n } = useTranslation();

  const recurso = useRecurso<readonly GameSummary[]>(
    useCallback(() => listGames(quizId), [quizId]),
    [quizId],
  );

  const fecha = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long', timeStyle: 'short' });

  return (
    <section aria-labelledby="partidas-titulo" className="border-line mt-12 border-t-2 pt-8">
      <h2 id="partidas-titulo" className="font-display text-h3 font-bold">
        {t('analytics.gamesTitle')}
      </h2>

      <VistaRecurso
        recurso={recurso}
        vacio={{ titulo: t('analytics.noGames'), pie: t('analytics.noGamesLead') }}
      >
        {(partidas) => (
          <ul className="mt-5 flex flex-col gap-3">
            {partidas.map((partida, indice) => {
              const estado = ESTADOS[partida.status as keyof typeof ESTADOS] ?? ESTADOS.ended;
              const abierta = partida.status === 'lobby' || partida.status === 'running';

              return (
                <li
                  key={partida.id}
                  className="entra rounded-card border-line bg-surface flex flex-wrap items-center gap-4 border-2 p-4"
                  style={{ '--retraso': `${Math.min(indice, 6) * 45}ms` } as React.CSSProperties}
                >
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/partidas/${partida.id}`}
                      className="font-display text-lead font-bold underline-offset-4 hover:underline"
                    >
                      {abierta
                        ? t('analytics.startedOn', {
                            date: fecha.format(new Date(partida.created_at)),
                          })
                        : t('analytics.playedOn', {
                            date: fecha.format(new Date(partida.created_at)),
                          })}
                    </Link>
                    <p className="text-micro text-text-dim mt-1 flex flex-wrap items-center gap-2">
                      <Glifo nombre={estado.glifo} size={16} />
                      {t(estado.clave)}
                      <span className="font-display font-bold tabular-nums">{partida.pin}</span>
                    </p>
                  </div>

                  {/* La vía de rescate: si se cierra la pestaña del proyector, esta es la única
                      forma de volver a la sala sin escribir la dirección a mano. */}
                  {abierta && (
                    <Link
                      to={`/host/${partida.pin}`}
                      className="text-body font-display rounded-chip border-edge bg-hivis text-on-hivis inline-flex min-h-11 items-center border-2 px-5 font-bold"
                    >
                      {t('analytics.backToRoom')}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </VistaRecurso>

      <Rotulo className="mt-4 block">{t('analytics.gamesHint')}</Rotulo>
    </section>
  );
}
